import { useCallback, useEffect, useMemo, useState } from 'react';
import styles from '../styles/TreeComponent.module.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquarePlus,faSquareMinus } from '@fortawesome/free-regular-svg-icons';
import NonExpandableSearchBox from './nonexpandablesearchbox';
import { useField } from 'formik';
import useApi from '../hooks/use-api';
import { useModalStatusContext } from '../contexts/modalstatuscontext';
import TreeSkeleton from './treeskeleton';
import InsetAlert from './insetalert';

const createVirtualGroupings = (nodes) => {
    let flattened = [];
    const seenPaths = new Set();

    nodes.forEach(node => {
        const pathSegments = node.path.split('.');
        const lastIndex = pathSegments.length - 1;
        let currentPath = '';

        pathSegments.forEach((segment,index) => {
            currentPath = index === 0 ? segment : `${currentPath}.${segment}`;

            if(!seenPaths.has(currentPath)){
                seenPaths.add(currentPath);

                if(index === lastIndex){
                    flattened.push({...node,level: index, isReal: true});
                }
                else{
                    //Virtual Node with generated id
                    flattened.push({id: segment + index,name: segment, path: currentPath, level : index, isReal: false});
                }
            }
        })
    });

    return flattened.sort((a,b) => a.path.localeCompare(b.path));
}

const getAncestorPaths = (path) => {
    const segments = path.split('.');
    return segments.map((_,index) => segments.slice(0,index + 1).join('.'));
}

const createRealDescendantLookup = (nodes, shouldIncludeNode = () => true) => {
    const descendantsByPath = new Map();

    nodes.forEach(node => {
        if(!node.isReal){
            descendantsByPath.set(node.path, []);
        }
    });

    nodes.forEach(node => {
        if(!node.isReal || !shouldIncludeNode(node)){
            return;
        }

        const segments = node.path.split('.');
        let ancestorPath = '';

        for(let index = 0; index < segments.length - 1; index++){
            ancestorPath = index === 0 ? segments[index] : `${ancestorPath}.${segments[index]}`;
            descendantsByPath.get(ancestorPath)?.push(node);
        }
    });

    return descendantsByPath;
}

const TreeComponent = ({name,objectName,actionName = 'Grant',retrieveDataEndpoint,extractDataFunction,searchboxid,disabled = false, includeImpliedSelections = false}) => {
    const [field,,helpers] = useField(name);

    const [collapsedPaths,setCollapsedPaths] = useState(new Set());
    const [searchTerm,setSearchTerm] = useState('');
    const {processing, error, getRequest} = useApi();
    const {setLoading,handleError} = useModalStatusContext();

    const [items,setItems] = useState([]);

    const itemByPath = useMemo(() => new Map(items.map(item => [item.path,item])),[items]);
    const itemById = useMemo(() => new Map(items.map(item => [item.id,item])),[items]);
    const virtualNodes = useMemo(() => createVirtualGroupings(items),[items]);
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    const selectedItemIds = useMemo(() => new Set(field.value ?? []),[field.value]);
    const realDescendantNodesByPath = useMemo(() => createRealDescendantLookup(virtualNodes),[virtualNodes]);
    const searchedRealDescendantNodesByPath = useMemo(() => {
        if(!normalizedSearchTerm){
            return realDescendantNodesByPath;
        }

        return createRealDescendantLookup(virtualNodes,node => node.path.toLowerCase().includes(normalizedSearchTerm));
    },[normalizedSearchTerm,realDescendantNodesByPath,virtualNodes]);

    const displayNodes = useMemo(() => {
        let updatedSet = virtualNodes;

        if(normalizedSearchTerm){
            let searchSet = new Set();

            virtualNodes.forEach(node => {
                if(node.path.toLowerCase().includes(normalizedSearchTerm)){
                    getAncestorPaths(node.path).forEach(ancestorPath => searchSet.add(ancestorPath));
                }
            });

            updatedSet = virtualNodes.filter(node => searchSet.has(node.path));
        }

        if(collapsedPaths.size === 0){
            return updatedSet;
        }

        return updatedSet.filter(node => {
            for(let collapsedPath of collapsedPaths){
                if(node.path.startsWith(`${collapsedPath}.`)){
                    return false;
                }
            }

            return true;
        });
    },[collapsedPaths,normalizedSearchTerm,virtualNodes]);

    const visibleRealDescendantNodesByPath = useMemo(() => createRealDescendantLookup(displayNodes),[displayNodes]);

    const sortSelections = useCallback((values) => {
        return [...values].sort((left,right) => `${left}`.localeCompare(`${right}`, undefined, { numeric: true }));
    },[])

    const getImpliedIds = useCallback((item) => {
        if(!item?.impliedPermissionPaths?.length){
            return [];
        }

        const impliedIds = new Set();
        const pendingPaths = [...item.impliedPermissionPaths];

        while(pendingPaths.length > 0){
            const impliedPath = pendingPaths.shift();
            const impliedItem = itemByPath.get(impliedPath);

            if(!impliedItem || impliedIds.has(impliedItem.id)){
                continue;
            }

            impliedIds.add(impliedItem.id);
            (impliedItem.impliedPermissionPaths ?? []).forEach(path => pendingPaths.push(path));
        }

        return Array.from(impliedIds);
    },[itemByPath])

    const expandSelectionsWithImplied = useCallback((selectedIds) => {
        const nextItems = new Set(selectedIds ?? []);

        if(!includeImpliedSelections){
            return sortSelections(nextItems);
        }

        selectedIds?.forEach(selectedId => {
            const selectedItem = itemById.get(selectedId);
            getImpliedIds(selectedItem).forEach(impliedId => nextItems.add(impliedId));
        });

        return sortSelections(nextItems);
    },[getImpliedIds,includeImpliedSelections,itemById,sortSelections])

    useEffect(() => {
        setLoading(true);

        const get = async () => {
            const [data, status] = await getRequest(retrieveDataEndpoint, {timestamp: Date.now()}); 
            if (status === 200) {
                let values = extractDataFunction(data);

                setItems(values);
                setLoading(false);
            }
            else{
                setItems([]);
                setLoading(false);
            }
        };

        get();
    },[]);

    useEffect(() => {
        if(error){
            handleError(error);
        }
    },[error])

    useEffect(() => {
        if(!includeImpliedSelections || items.length === 0 || !field.value?.length){
            return;
        }

        const expandedSelections = expandSelectionsWithImplied(field.value);
        const currentSelections = sortSelections(field.value);

        if(expandedSelections.length !== currentSelections.length || expandedSelections.some((id,index) => id !== currentSelections[index])){
            helpers.setValue(expandedSelections);
        }
    },[expandSelectionsWithImplied,field.value,helpers,includeImpliedSelections,items,sortSelections]);

    const toggleCollapse = (path) => {
        if(!path){
            return;
        }

        setCollapsedPaths(prev => {
           const newCollapsedPaths = new Set(prev);
           
           if(newCollapsedPaths.has(path)){
            newCollapsedPaths.delete(path);
           }
           else{
            newCollapsedPaths.add(path);
           }

           return newCollapsedPaths;
        });
    }

    const getSelectableRealChildren = useCallback((path) => {
        if(normalizedSearchTerm){
            return collapsedPaths.has(path)
                ? searchedRealDescendantNodesByPath.get(path) ?? []
                : visibleRealDescendantNodesByPath.get(path) ?? [];
        }

        return realDescendantNodesByPath.get(path) ?? [];
    },[collapsedPaths,normalizedSearchTerm,realDescendantNodesByPath,searchedRealDescendantNodesByPath,visibleRealDescendantNodesByPath]);

    const childrenSelected = useCallback((path) => {
        const children = getSelectableRealChildren(path);

        return children.length > 0 && children.every(node => selectedItemIds.has(node.id));
    },[getSelectableRealChildren,selectedItemIds])

    const toggleSelection = (item) => {
        const currentSelections = field.value ?? [];

        if(item.isReal){
            if(selectedItemIds.has(item.id)){
                const newItems = sortSelections(currentSelections.filter(selItem => selItem !== item.id));
                helpers.setValue(newItems);
            }
            else{
                const newItems = new Set([...currentSelections,item.id]);

                if(includeImpliedSelections){
                    getImpliedIds(item).forEach(impliedId => newItems.add(impliedId));
                }

                helpers.setValue(sortSelections(newItems));
            }
        }
        else{
            const childrenToUpdate = getSelectableRealChildren(item.path);
            let newItems = new Set([...currentSelections]);

            const shouldSelect = childrenToUpdate.some(child => !newItems.has(child.id));

            childrenToUpdate.forEach(child => {
                if(shouldSelect){
                    newItems.add(child.id);

                    if(includeImpliedSelections){
                        getImpliedIds(child).forEach(impliedId => newItems.add(impliedId));
                    }
                }
                else{
                    newItems.delete(child.id)
                }
            })

            helpers.setValue(sortSelections(newItems));
        }
    }

    return(
        <>
        {
            processing
            ?
                <TreeSkeleton />
            :
                error
                ?
                    <InsetAlert error={error} dismissible={false} style={{width: '100%'}}/>
                :
                <>
                <div className='mb-2' >
            <div className={styles.searchContainer}>
                <NonExpandableSearchBox id={searchboxid} style={{color: 'black'}} placeholder={`Search ${objectName}'s`} searchTerm={searchTerm} setSearchTerm={setSearchTerm} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}/>
            </div>
        </div>
        <div className={styles.treeBody}>
            <div style={{width: '95%'}}>
                <div className={styles.treeHeader}>
                    <strong>{`${objectName}s`}</strong>
                    <strong className={styles.treeHeaderRight}>{actionName}</strong>
                </div>
                <div >
                    {
                        displayNodes.length ?
                            displayNodes.map((node,index) => (
                                <div key={index} className={`${styles.treeNode} ${index === displayNodes.length - 1 || index === 0 ? styles.noBorder : ''}`} style={{paddingLeft: `${node.level * 20}px`}}>
                                    {
                                        !node.isReal
                                        ?
                                            collapsedPaths.has(node.path)
                                            ?
                                                <button className={`${styles.collapseBtn} me-1`} type='button' title='Expand' aria-label='Expand' onClick={() => !node.isReal && toggleCollapse(node.path)}>
                                                    <FontAwesomeIcon icon={faSquarePlus}  />
                                                </button>
                                            :
                                                <button className={`${styles.collapseBtn} me-1`} type='button' title='Collapse' aria-label='Collapse' onClick={() => !node.isReal && toggleCollapse(node.path)}>
                                                    <FontAwesomeIcon icon={faSquareMinus}  />
                                                </button>
                                        :
                                            null
                                    }
                                    <div className='d-flex justify-content-between' style={{width: '100%'}}>
                                        <span>
                                            {node.name}
                                        </span>
                                        <div className={styles.treeHeaderRight}>
                                            <input type='checkbox' disabled={disabled} onChange={() => toggleSelection(node) } checked={selectedItemIds.has(node.id) || (!node.isReal && childrenSelected(node.path))}/>
                                        </div>
                                    </div>
                                </div>
                                
                            ))
                            :
                                searchTerm ?
                                    <div className='d-flex justify-content-center mt-1'>
                                        <span>No Results Found</span>
                                    </div>
                                :
                                    <div className='d-flex justify-content-center mt-1'>
                                        <span>No Items Exist</span>
                                    </div>
                        }
                
                </div>
            </div>
        </div>
                </>
        }
        </>
    )
}

export default TreeComponent;
